import { useEffect, useRef } from 'react';
import { Send, Bell, DoorOpen, MessageSquare, CircleDot } from 'lucide-react';

// The ONE resident-side conversation thread. Shared by the resident portal's
// Visitors screen and the no-login link that opens a single conversation, so
// both render exactly the same thread. Every label is passed IN by the caller
// (property terms only — visitor, resident, unit, entrance).
export const VisitorConversation = ({
    messages = [],
    labels = {},
    input = '',
    onInputChange,
    onSend,
    onNotify,
    live = false,
    closed = false,
    notifiedAt = null,
    busy = false,
    prefix = 'vc',
}) => {
    const endRef = useRef(null);
    useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length]);

    const bubble = (m, i) => {
        const mine = m.sender_role === 'resident';
        if (m.kind === 'action') {
            return (
                <div key={m.id || i} className="flex justify-center" data-testid={`${prefix}-action`}>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/25 bg-cyan-500/10 px-3 py-1 text-[11px] text-cyan-200">
                        <DoorOpen className="h-3 w-3" />{labels.chose} {m.text}
                    </span>
                </div>
            );
        }
        return (
            <div key={m.id || i} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div data-testid={`${prefix}-bubble-${mine ? 'resident' : 'visitor'}`}
                    className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm ${mine
                        ? 'bg-gradient-to-br from-cyan-500 to-cyan-400 text-slate-950 rounded-br-sm'
                        : 'bg-slate-900/70 text-slate-100 border border-white/10 rounded-bl-sm'}`}>
                    <p className={`text-[10px] uppercase tracking-widest mb-1 ${mine ? 'text-slate-900/60' : 'text-cyan-300/80'}`}>
                        {m.kind === 'auto_response' ? labels.autoReply : (mine ? labels.you : labels.visitor)}
                    </p>
                    {m.text}
                </div>
            </div>
        );
    };

    return (
        <div data-testid={`${prefix}-thread`}>
            <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium border ${live
                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30'
                    : 'bg-slate-700/40 text-slate-400 border-white/10'}`} data-testid={`${prefix}-presence`}>
                    <CircleDot className={`h-3 w-3 ${live ? 'animate-pulse' : ''}`} />
                    {live ? labels.atDoorNow : labels.notAtDoor}
                </span>
                {notifiedAt && !closed && (
                    <span className="text-[11px] text-cyan-300/80" data-testid={`${prefix}-notified`}>{labels.notifiedSent}</span>
                )}
            </div>

            <div className="max-h-[22rem] min-h-[9rem] overflow-y-auto rounded-2xl bg-white/[0.02] border border-white/10 p-3 space-y-2.5"
                data-testid={`${prefix}-messages`}>
                {messages.length === 0 ? (
                    <p className="py-8 text-center text-xs text-slate-500" data-testid={`${prefix}-empty`}>{labels.empty}</p>
                ) : messages.map(bubble)}
                <div ref={endRef} />
            </div>

            {closed ? (
                <p className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-3 text-center text-xs text-slate-400"
                    data-testid={`${prefix}-closed`}>{labels.closed}</p>
            ) : (
                <>
                    <div className="mt-3 flex gap-2">
                        <input value={input} onChange={(e) => onInputChange(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && onSend()}
                            placeholder={labels.placeholder} data-testid={`${prefix}-input`}
                            className="flex-1 rounded-xl border border-white/10 bg-slate-900 px-3.5 py-3 text-sm text-white placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none" />
                        <button type="button" onClick={onSend} disabled={busy || !input.trim()}
                            data-testid={`${prefix}-send`}
                            className="rounded-xl bg-gradient-to-r from-cyan-500 to-cyan-400 px-4 font-bold text-slate-950 shadow-lg shadow-cyan-500/20 transition-transform active:scale-95 disabled:opacity-40 motion-reduce:active:scale-100">
                            <Send className="h-5 w-5" />
                        </button>
                    </div>
                    {onNotify && (
                        <button type="button" onClick={onNotify} disabled={busy} data-testid={`${prefix}-notify`}
                            className="mt-2.5 w-full flex items-center justify-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 py-2.5 text-sm text-cyan-200 transition-colors hover:bg-cyan-500/20 disabled:opacity-50">
                            <Bell className="h-4 w-4" />{labels.notifyVisitor}
                        </button>
                    )}
                    <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-500">
                        <MessageSquare className="mt-0.5 h-3 w-3 shrink-0" />{labels.privacyNote}
                    </p>
                </>
            )}
        </div>
    );
};

export default VisitorConversation;
